import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// API CONFIG
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_SECRET_KEY = process.env.REACT_APP_API_SECRET_KEY;

function ExcelExportViewer() {
  const location = useLocation();
  const { tenant_id } = useParams();

  /* ---------------- DATA STATE ---------------- */
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  /* ---------------- FILTER STATE ---------------- */
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    fromDate: "",
    toDate: "",
  });
  const [isSearchApplied, setIsSearchApplied] = useState(false);

  /* ---------------- TABLE STATE ---------------- */
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortAsc, setSortAsc] = useState(false);

  /* ---------------- FETCH DATA ---------------- */
  useEffect(() => {
    let url = "";

    if (location.pathname === "/excel-export") {
      url = `${API_BASE_URL}/member/excel-export`;
    } else if (location.pathname.startsWith("/excel-export/")) {
      url = `${API_BASE_URL}/member/excel-export/${tenant_id}`;
    }

    if (!url) return;

    setLoading(true);

    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "api-key": API_SECRET_KEY,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Unauthorized or server error");
        return res.json();
      })
      .then((data) => {
        setRows(data.status === 1 && data.data ? data.data : []);
      })
      .catch((err) => {
        console.error("Fetch error:", err);
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, [location.pathname, tenant_id]);

  /* ---------------- FILTER LOGIC ---------------- */
  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (!row.created_at) return false;

      const rowDate = new Date(row.created_at);

      const fromMatch = appliedFilters.fromDate
        ? rowDate >= new Date(appliedFilters.fromDate)
        : true;

      const toMatch = appliedFilters.toDate
        ? rowDate <= new Date(`${appliedFilters.toDate}T23:59:59`)
        : true;

      return fromMatch && toMatch;
    });
  }, [rows, appliedFilters]);

  /* ---------------- SORT LOGIC ---------------- */
  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      const d1 = new Date(a.created_at || 0);
      const d2 = new Date(b.created_at || 0);
      return sortAsc ? d1 - d2 : d2 - d1;
    });
  }, [filteredRows, sortAsc]);

  /* ---------------- PAGINATION ---------------- */
  const totalPages = Math.ceil(sortedRows.length / pageSize);
  const paginatedRows = sortedRows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const startEntry =
    sortedRows.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endEntry = Math.min(currentPage * pageSize, sortedRows.length);

  /* ---------------- ACTIONS ---------------- */
  const handleSearch = () => {
    setAppliedFilters({ fromDate, toDate });
    setCurrentPage(1);
    setIsSearchApplied(true);
  };

  const handleClear = () => {
    setFromDate("");
    setToDate("");
    setAppliedFilters({ fromDate: "", toDate: "" });
    setCurrentPage(1);
    setIsSearchApplied(false);
  };
  const handleExportExcel = () => {
    if (sortedRows.length === 0) return;

    const exportData = sortedRows.map((row, index) => ({
      "S.No": index + 1,
      Date: row.created_at || "",
      "Exported By": row.action_by?.member_name || "",
      Description: row.action_name || "",
      "File Name": row.file_name || "Excel Export",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Export Logs");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const file = new Blob([excelBuffer], {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const fileName = `excel_export_logs_${new Date()
      .toISOString()
      .slice(0, 10)}.xlsx`;

    saveAs(file, fileName);
  };

  /* ---------------- UI ---------------- */
  return (
    <div className="container-fluid py-3">

      {/* TOP CONTROLS */}
      <div className="d-flex align-items-center justify-content-between mb-3 bg-light p-2 rounded shadow-sm">
        {/* LEFT SIDE – PAGE SIZE */}
        <div className="d-flex align-items-center gap-2">
          <span className="small fw-bold text-secondary">Show</span>

          <select
            className="form-select form-select-sm"
            style={{ width: "80px" }}
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            {[10, 15, 25, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>

          <span className="small fw-bold text-secondary">entries</span>
        </div>

        {/* RIGHT SIDE – DATE FILTERS */}
        {/* RIGHT SIDE – DATE FILTERS */}
        <div className="d-flex align-items-center gap-2">
          <label className="small fw-bold mb-0">From</label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            disabled={isSearchApplied}
            style={{ width: "150px" }}
          />

          <label className="small fw-bold mb-0">To</label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            disabled={isSearchApplied}
            style={{ width: "150px" }}
          />

          <button
            className={`btn btn-sm fw-bold ${isSearchApplied ? "btn-danger" : "btn-primary"
              }`}
            onClick={isSearchApplied ? handleClear : handleSearch}
          >
            {isSearchApplied ? "Clear" : "Search"}
          </button>

          {/* EXPORT BUTTON */}
          <button
            className="btn btn-sm btn-success fw-bold"
            onClick={handleExportExcel}
            disabled={sortedRows.length === 0}
          >
            Export 
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="card border-0 shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>S.No</th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => setSortAsc(!sortAsc)}
                >
                  Date {sortAsc ? "▲" : "▼"}
                </th>
                <th>Exported By</th>
                <th>Description</th>
                <th>File Name</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    Loading...
                  </td>
                </tr>
              ) : paginatedRows.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-4 text-muted">
                    No export logs found
                  </td>
                </tr>
              ) : (
                paginatedRows.map((row, index) => (
                  <tr key={index}>
                    <td>{startEntry + index}</td>
                    <td>{row.created_at || "-"}</td>

                    <td>
                      {row.tenant_domain && row.action_by?.member_id ? (
                        <a
                          href={`${row.tenant_domain}/portal/members/member-details/${row.action_by.member_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none fw-medium"
                        >
                          {row.action_by.member_name}
                        </a>
                      ) : (
                        row.action_by?.member_name || "-"
                      )}
                    </td>

                    <td className="text-capitalize">
                      {row.action_name || "-"}
                    </td>

                    <td className="text-truncate" style={{ maxWidth: "220px" }}>
                      {row.file_name || "Excel Export"}
                    </td>

                    <td className="text-center">
                      {row.s3_file_url ? (
                        <a
                          href={row.s3_file_url}
                          download
                          className="btn btn-sm btn-outline-primary"
                        >
                          Download
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PAGINATION */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <span className="small text-secondary">
          Showing <strong>{startEntry}</strong> to{" "}
          <strong>{endEntry}</strong> of{" "}
          <strong>{sortedRows.length}</strong> entries
        </span>

        <ul className="pagination pagination-sm mb-0">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Previous
            </button>
          </li>

          {[...Array(totalPages)].map((_, i) => (
            <li
              key={i}
              className={`page-item ${currentPage === i + 1 ? "active" : ""
                }`}
            >
              <button
                className="page-link"
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            </li>
          ))}

          <li
            className={`page-item ${currentPage === totalPages || totalPages === 0
              ? "disabled"
              : ""
              }`}
          >
            <button
              className="page-link"
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
}

export default ExcelExportViewer;